import LZString from 'lz-string'
import { normalizePath } from './routerUtils'

/**
 * Compresses filter data to a URL-safe string.
 */
export function encodeFilters(filters: unknown): string {
	if (!filters) {
		return ''
	}
	const json = JSON.stringify(filters)
	return LZString.compressToEncodedURIComponent(json)
}

/**
 * Decompresses filter data from a URL parameter.
 */
export function decodeFilters<T>(value: string | undefined): T | undefined {
	if (!value) {
		return undefined
	}
	try {
		let result: unknown = undefined
		if (value.startsWith('[') || value.startsWith('{')) {
			result = JSON.parse(decodeURIComponent(value))
		} else {
			const json = LZString.decompressFromEncodedURIComponent(value)
			result = json ? JSON.parse(json) : undefined
		}
		if (Array.isArray(result) && result.length > 1000) {
			result = result.slice(0, 1000)
		}
		return result as T | undefined
	} catch (e) {
		console.error('Failed to decode filters:', e)
		return undefined
	}
}

/**
 * Synchronously reads filters from the current URL query parameters.
 * Used by stores and hooks to initialize filter state before the first render,
 * preventing a flash of unfiltered content on page refresh.
 */
export function getInitialFiltersFromUrl<T>(): T[] {
	// getInitialFiltersFromUrl: SSR and other non-browser runtimes have no window/location
	if (typeof window === 'undefined' || typeof window.location === 'undefined') {
		return []
	}
	try {
		const params = new URLSearchParams(window.location.search)
		const filtersParam = params.get('filters')
		if (filtersParam) {
			const decoded = decodeFilters<T[]>(filtersParam)
			if (decoded && Array.isArray(decoded) && decoded.length > 0) {
				return decoded
			}
		}
	} catch {
		// Ignore decode errors, fall back to empty
	}
	return []
}

/**
 * Extracts a stable section identifier from a path.
 * Used for detecting when to clear filters during navigation.
 */
export function getRouteSection(path: string): string {
	try {
		// Use the FULL normalized path to ensure filters clear even between sub-tabs
		return normalizePath(path)
	} catch {
		return ''
	}
}

export const filterLimitFlags = {
	justWarnedPasteLimit: false,
}
